import { forwardRef, RefObject, useMemo } from 'react';

import { Button, FormControl, FormLabel, Portal, Stack, Text } from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { format, parse, parseISO } from 'date-fns';

import { useQueryClient } from '@tanstack/react-query';

import {
  CheckOutline,
  CustomAlert,
  DatePickerInput,
  DEFAULT_DATE_FORMAT,
  useToast,
} from '@aslanpay/portal-common-lib';

import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useTerminateIndividual from 'app/hooks/useTerminateIndividual';

import { terminateEmployeeSchema } from 'app/utils/schemas';

import {
  EditEmployeeTerminationDateFormFields,
  EditEmployeeTerminationDateFormProps,
} from './types';

const EditEmployeeTerminationDateForm = forwardRef<
  HTMLElement,
  EditEmployeeTerminationDateFormProps
>(({ employee, onConfirm, onClose }, ref) => {
  const queryClient = useQueryClient();

  const toast = useToast();

  const { data: payCalendar } = useFetchPayCalendar(employee.payCalendarId);

  // Calculate max date: last generated pay cycle's cutoff date
  // (only a year's worth of pay cycles are generated, so this will be ~1 year from now)
  const maxDate = useMemo(() => {
    if (!payCalendar?.payCycles || payCalendar.payCycles.length === 0) {
      return undefined;
    }
    // Find the last pay cycle by latest start date
    const lastPayCycle = payCalendar.payCycles.reduce((latest, cycle) => {
      const latestStart = parseISO(latest.startDate);
      const cycleStart = parseISO(cycle.startDate);
      return cycleStart > latestStart ? cycle : latest;
    });
    return lastPayCycle.cutoffDate ? parseISO(lastPayCycle.cutoffDate) : undefined;
  }, [payCalendar]);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<EditEmployeeTerminationDateFormFields>({
    mode: 'onBlur',
    defaultValues: { terminationDate: employee.terminationDate },
    resolver: yupResolver(terminateEmployeeSchema),
  });

  const { mutate: terminateEmployee, isLoading: isTerminatingEmployee } = useTerminateIndividual({
    onSuccess: (data) => {
      const parsedDate = data.terminationDate
        ? format(parse(data.terminationDate, 'yyyy-MM-dd', new Date()), DEFAULT_DATE_FORMAT)
        : '';

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Leave date edited',
        description: `The employee will be marked as left in the system after ${
          parsedDate || 'this date'
        }`,
      });

      queryClient.invalidateQueries({ queryKey: ['fetchIndividual', data.id] });

      onConfirm();
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the employee leave date was not edited. Please try again.',
      });
    },
  });

  const handleTerminateEmployee = (data: EditEmployeeTerminationDateFormFields) => {
    terminateEmployee({
      id: employee.id,
      terminationDate: data.terminationDate,
    });
  };

  return (
    <form
      id="edit-employee-termination-date-form"
      onSubmit={handleSubmit(handleTerminateEmployee)}
      noValidate
    >
      <Stack spacing="20px">
        <CustomAlert
          variant="simple"
          status="info"
          title={
            <>
              <Text textStyle="extraSmallMedium">What is the leave date?</Text>
              <Text textStyle="extraSmallRegular">
                This is to mark that the employee has left employment and can no longer accrue a
                salary via Aslan. The employee can access all services until 23:59:59 on the
                designated leave date. Upon the leave date passing, the employees card is
                automatically cancelled.
              </Text>
            </>
          }
        />
        <FormControl>
          <FormLabel>Leave date</FormLabel>
          <Controller
            control={control}
            name="terminationDate"
            render={({ field: { onChange, onBlur, value } }) => {
              return (
                <DatePickerInput
                  placeholder="Set a date"
                  defaultValue={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={maxDate}
                />
              );
            }}
          />
        </FormControl>
      </Stack>
      <Portal containerRef={ref as RefObject<HTMLElement>}>
        <Button
          form="edit-employee-termination-date-form"
          type="submit"
          size="sm"
          rightIcon={<CheckOutline boxSize="20px" />}
          isLoading={isTerminatingEmployee}
          isDisabled={!isValid}
        >
          Confirm
        </Button>
      </Portal>
    </form>
  );
});

EditEmployeeTerminationDateForm.displayName = 'EditEmployeeTerminationDateForm';

export default EditEmployeeTerminationDateForm;
